import { makeApiRequestPromise } from "../../lib/api";
import { toFriendlyApiError } from "../../lib/apiError";
import type {
  GetInsightsAggregateRequest,
  InsightsAggregateResponse,
  MetadataKey,
  MetadataValue,
  MetadataValuesResponse,
  ListTracesResponse,
  SourceDetailResponse,
  TraceItem,
} from "./types";
import type { MetadataFilterContext } from "./utils/metadataFilters";

/**
 * Fetch all metadata keys with their types for the authenticated user's domain.
 */
export const fetchMetadataKeys = async (): Promise<MetadataKey[]> => {
  try {
    const response = await makeApiRequestPromise({
      method: "POST",
      path: `/api/v1/metadata/keys`,
      body: {},
    });
    const result = response as { keys: MetadataKey[] };
    return result.keys || [];
  } catch (error: unknown) {
    console.error("Failed to fetch metadata keys:", error);
    throw new Error("Failed to fetch metadata keys");
  }
};

/**
 * Fetch distinct values for multiple metadata keys, each with the number of
 * traces behind it.
 *
 * When `filters` is supplied, the backend narrows each key's values to those
 * appearing on rows that match the active filter context (faceted search). The
 * caller is responsible for stripping each key's own tag filter — see
 * `buildMetadataFilterContext`.
 *
 * The wire shape keeps values and counts in two parallel maps so `values` stays
 * backwards-compatible for API-key callers; they are zipped here so the rest of
 * the app only ever sees one list per key. A response without `counts` (an older
 * server mid-deploy) yields values with no count, which render without one.
 */
export const fetchMetadataValues = async (
  keys: string[],
  filters?: MetadataFilterContext,
  limit?: number
): Promise<Record<string, MetadataValue[]>> => {
  try {
    const response = await makeApiRequestPromise({
      method: "POST",
      path: `/api/v1/metadata/values`,
      body: {
        keys,
        ...(filters ? { filters } : {}),
        ...(limit !== undefined ? { limit } : {}),
      },
    });
    const valuesResponse = response as MetadataValuesResponse;
    return Object.fromEntries(
      Object.entries(valuesResponse.values || {}).map(([key, values]) => [
        key,
        values.map((value) => ({
          value,
          count: valuesResponse.counts?.[key]?.[value],
        })),
      ])
    );
  } catch (error: unknown) {
    console.error("Failed to fetch metadata values:", error);
    throw new Error("Failed to fetch metadata values");
  }
};

/**
 * Fetch distinct criteria values for the authenticated user's domain, ordered by
 * most recent evaluation date.
 *
 * When `filters` is supplied, the backend narrows the criteria list to those
 * appearing on rows that match the active filter context (faceted search). The
 * caller strips the criteria filter itself — see `buildCriteriaFilterContext`.
 */
export const fetchCriteriaValues = async (
  filters?: MetadataFilterContext
): Promise<string[]> => {
  try {
    const response = await makeApiRequestPromise({
      method: "POST",
      path: "/api/v1/metadata/criteria",
      body: { ...(filters ? { filters } : {}) },
    });
    const result = response as { values: string[] };
    return result.values || [];
  } catch (error: unknown) {
    console.error("Failed to fetch criteria values:", error);
    throw new Error("Failed to fetch criteria values");
  }
};

/**
 * Get server-aggregated insights series for the chart views.
 * Mirrors the /evaluations filter surface but returns per-criteria,
 * per-time-bucket aggregates over up to the 100k most recent matches.
 */
export const getInsightsAggregate = async (
  params: GetInsightsAggregateRequest,
): Promise<InsightsAggregateResponse> => {
  const queryParams: Record<string, string | number | string[] | undefined> = {};
  if (params.start_date) queryParams.start_date = params.start_date;
  if (params.end_date) queryParams.end_date = params.end_date;
  if (params.min_score != null) queryParams.min_score = params.min_score;
  if (params.max_score != null) queryParams.max_score = params.max_score;
  if (params.criteria?.length) queryParams.criteria = params.criteria;
  if (params.tag?.length) queryParams.tag = params.tag;
  if (params.group_by) queryParams.group_by = params.group_by;
  if (params.group_limit != null) queryParams.group_limit = params.group_limit;
  if (params.histogram) queryParams.histogram = "true";
  if (params.below_threshold != null) {
    queryParams.below_threshold = params.below_threshold;
  }

  try {
    const response = await makeApiRequestPromise({
      method: "GET",
      path: "/api/v1/insights/aggregate",
      params: queryParams,
    });
    return response as InsightsAggregateResponse;
  } catch (error: unknown) {
    throw toFriendlyApiError(error, "Failed to load insights.");
  }
};

/**
 * List traces with cursor-based pagination and the full structured filter set.
 *
 * Mirrors the /insights/aggregate filter surface (date range, score range,
 * criteria, tags) so a chart drill-down on the Insights page lands on the same
 * slice of traces. `tag` entries use the `key:value1,value2` wire format (same
 * as getInsightsAggregate). Array params (`criteria`, `tag`) are repeated by the
 * shared paramsSerializer in lib/api.ts, matching the endpoint's `list[str]`
 * query params.
 */
export const listTraces = async (params: {
  page_token?: string;
  page_size?: number;
  start_date?: string;
  end_date?: string;
  min_score?: number;
  max_score?: number;
  criteria?: string[];
  tag?: string[];
}): Promise<ListTracesResponse> => {
  const queryParams: Record<string, string | number | string[] | undefined> = {};
  if (params.page_token) queryParams.page_token = params.page_token;
  if (params.page_size) queryParams.page_size = params.page_size;
  if (params.start_date) queryParams.start_date = params.start_date;
  if (params.end_date) queryParams.end_date = params.end_date;
  if (params.min_score != null) queryParams.min_score = params.min_score;
  if (params.max_score != null) queryParams.max_score = params.max_score;
  if (params.criteria?.length) queryParams.criteria = params.criteria;
  if (params.tag?.length) queryParams.tag = params.tag;

  return makeApiRequestPromise({
    method: "GET",
    path: `/api/v1/traces`,
    params: queryParams,
  }) as Promise<ListTracesResponse>;
};

/**
 * Fetch a single trace with full details by id. Powers deep links into the
 * Traces page (e.g. from the failure-clusters panel) where the target trace is
 * frequently not on the currently loaded page.
 */
export const getTrace = async (traceId: string): Promise<TraceItem> => {
  return makeApiRequestPromise({
    method: "GET",
    path: `/api/v1/traces/${encodeURIComponent(traceId)}`,
  }) as Promise<TraceItem>;
};

export const attachRating = async (traceId: string, rating: boolean): Promise<void> => {
  await makeApiRequestPromise({
    method: "POST",
    path: "/api/v1/annotations/rating",
    body: { trace_id: traceId, rating },
  });
};

export const attachComment = async (traceId: string, comment: string): Promise<void> => {
  await makeApiRequestPromise({
    method: "POST",
    path: "/api/v1/annotations/comment",
    body: { trace_id: traceId, comment },
  });
};

/**
 * Fetch source details by type and id.
 * Supports: document, memory, annotation, uploaded_evaluation.
 */
export const fetchSourceDetail = async (
  sourceType: string,
  sourceId: string
): Promise<SourceDetailResponse> => {
  const response = await makeApiRequestPromise({
    method: "GET",
    path: `/api/v1/sources/${encodeURIComponent(sourceType)}/${encodeURIComponent(sourceId)}`,
  });
  return response as SourceDetailResponse;
};
